"use client";

// BootstrappedStatsigProvider.tsx
import type { JSX } from "react";
import { useEffect, type PropsWithChildren } from "react";
import { StatsigProvider, useClientBootstrapInit, StatsigClient } from "@statsig/react-bindings";
import type { StatsigUser } from "../utils/StatsigUtils"; // Use your own interface
import { CookieUtils } from "../utils/CookieUtils";
import { UIUtils } from "../utils/UIUtils";
import { PublicConfig } from "../PublicConfig";

type Props = PropsWithChildren & {
    readonly clientSdkKey: string;
    readonly user: StatsigUser;
    readonly values: string | null;
};

let globalClient: StatsigClient | null = null;

export default function BootstrappedStatsigProvider({ clientSdkKey, user, values, children }: Props): JSX.Element {
    const client = useClientBootstrapInit(clientSdkKey, user, values || "", {
        environment: { tier: PublicConfig.ENVIRONMENT },
        networkConfig: { api: PublicConfig.STATSIG_API_PROXY_URL }
    });

    // ENG-17341: re-apply this page's bootstrap when the SDK handed us a pre-existing client.
    // `useClientBootstrapInit` -> `useStatsigInternalClientFactoryBootstrap` short-circuits on
    // `_getInstance(sdkKey)`: when a StatsigClient for this SDK key already exists -- which is the case
    // for every client-side App Router transition, e.g. the quote form's `router.push` to thank-you --
    // the freshly computed `user`/`values` props are silently discarded and the client keeps serving the
    // previous page's user. Re-apply them *synchronously*:
    //   - `dataAdapter.setData(values)` parses the payload, reads its echoed `user`, and stores the
    //     bootstrap in the in-memory cache under that user's cache key (userID + customIDs only).
    //   - `updateUserSync(user)` then resolves the very same cache key, so `getDataSync` hits the
    //     `Bootstrap` entry, the store is populated in the same tick, and the SDK returns *before*
    //     `_runPostUpdate` (bootstrap sources skip the background refresh) -- no network request, and
    //     no window where the store holds no values. `updateUserAsync` would instead blank the store
    //     for a full round trip, flipping every layer-driven UI to its defaults and back.
    // Runs after `StatsigProvider` has attached its `values_updated` listener (child effects flush
    // before the parent's), so the single emit re-renders consumers with the corrected evaluation.
    // Guarded on a material user difference so a client this provider just constructed on a fresh page
    // load never re-applies, and so the main quote flow is untouched.
    useEffect(() => {
        if (!values) return;

        const currentUser = client.getContext().user;
        const isStaleUser = currentUser?.userID !== user.userID || (!!user.customIDs?.accountID && currentUser?.customIDs?.accountID !== user.customIDs.accountID);

        if (!isStaleUser) return;

        client.dataAdapter.setData(values);
        client.updateUserSync(user);
    }, [client, user, values]);

    useEffect(() => {
        const tuidValue = CookieUtils.get("tuid");
        if (!!user.userID && tuidValue !== user.userID) {
            CookieUtils.set("tuid", user.userID, { expires: 365 });
        }
    }, [user]);

    useEffect(() => {
        const testAccountId = UIUtils.getCaseInsensitiveValue(new URLSearchParams(window.location.search), "testAccountId");
        if (testAccountId && CookieUtils.get("testAccountId") !== testAccountId) {
            CookieUtils.set("testAccountId", testAccountId); // no expires → session cookie
        }
    }, []);

    useEffect(() => {
        globalClient = client;
        return () => {
            globalClient = null;
        };
    }, [client]);

    return values ? <StatsigProvider client={client}>{children}</StatsigProvider> : <>{children}</>;
}

export const getGlobalStatsigClient = (): StatsigClient | null => globalClient;
